import { StepConfig } from '@/types';
import { RedeemFlowResponse } from '@/types/Redeem';

/**
 * Paths & Prerequisites can differ between partners,
 * so each partner gets its own STEP config.
 */

export const STEP: Record<string, StepConfig> = {
  EMAIL: {
    path: '/email',
    prereqs: [],
  },
  ACCOUNT: {
    path: '/account',
    prereqs: ['email'],
  },
  DELIVERY: {
    path: '/delivery',
    prereqs: ['netsuiteOrderId'],
  },
  REVIEW: {
    path: '/review',
    prereqs: ['sku'],
  },
  SELECT: {
    path: '/select',
    prereqs: ['netsuiteOrderId'],
  },
  SIZING: {
    path: '/sizing',
    prereqs: ['netsuiteOrderId'],
  },
  SUMMARY: {
    path: '/summary',
    prereqs: [],
  },
  REVIEW_ENROLLMENT: {
    path: '/review-enrollment',
    prereqs: ['sizingKit'],
  },
};

/**
 * Metrics
 */

export const DELIVERY_METRIC_NAME = 'delivery';
export const SELECTIONS_METRIC_NAME = 'selections';
export enum MetricStep {
  DELIVERY_SUBMITTED = 'delivery_submitted',
  DELIVERY_SUCCESS = 'delivery_success',
  DELIVERY_ERROR = 'delivery_error',
  SELECTIONS_SUBMITTED = 'selections_submitted',
  SELECTIONS_SUCCESS = 'selections_success',
  SELECTIONS_ERROR = 'selections_error',
}

/**
 * Static, structural configuration for each redeem flow. Anything that
 * varies per code at runtime (e.g. `discountPercent`) is NOT here — that
 * lives on the `meta` of `RedeemFlowResponse` returned by the
 * `validateRedeemCode` API and gets passed to `t()` as ICU values at
 * render time.
 *
 * Translation keys (rather than literal copy) keep the source of truth
 * for strings in Phrase. Behavior (submit handling, analytics, etc.)
 * lives at the call site, dispatched off `flow.type`.
 */
export interface RedeemFlowCopy {
  titleKey: string;
  /**
   * Ordered list of translation keys, one per paragraph. Rendered stacked
   * by the step layout. Multiple keys (rather than `\n\n` inside one key)
   * let a partner-specific intro paragraph swap independently of the
   * shared remainder — e.g. GLP-1 audience copy + common sizing-kit copy.
   */
  descriptionKeys: readonly [string, ...string[]];
}

/**
 * The checkbox config — when absent, the form renders without a
 * checkbox. Copy keys live inside this object so that omitting the
 * checkbox automatically omits its copy: there's no way to end up with
 * a flow that has a checkbox but no label, or stale copy keys hanging
 * around on a checkbox-less flow.
 */
export interface RedeemFlowCheckbox {
  kind: 'marketing_opt_in' | 'discount_consent';
  copy: {
    labelKey: string;
    descriptionKey: string;
  };
}

export interface RedeemFlow {
  copy: RedeemFlowCopy;
  checkbox?: RedeemFlowCheckbox;
  addressForm?: {
    fieldProps: Record<string, { labelKey: string }>;
  };
}

export const REDEEM_FLOWS = {
  free_sizing_kit: {
    copy: {
      titleKey: 'eop_redeem_free_sizing_kit_title',
      descriptionKeys: ['eop_redeem_free_sizing_kit_description'],
    },
    checkbox: {
      kind: 'marketing_opt_in',
      copy: {
        labelKey: 'eop_redeem_marketing_optin_label',
        descriptionKey: 'eop_redeem_marketing_optin_description',
      },
    },
  },
  free_sizing_kit_plus_discount: {
    copy: {
      titleKey: 'eop_redeem_free_sizing_kit_discount_title',
      descriptionKeys: [
        // Intro is specific to Lilly, and will likely lift this out into a partner config once we onboard more partners
        'eop_redeem_free_sizing_kit_discount_description_intro_glp1',
        'eop_redeem_free_sizing_kit_discount_description_common',
      ],
    },
    checkbox: {
      kind: 'discount_consent',
      copy: {
        labelKey: 'eop_redeem_discount_consent_label',
        descriptionKey: 'eop_redeem_discount_consent_description',
      },
    },
  },
} as const satisfies Record<RedeemFlowResponse['type'], RedeemFlow>;

/**
 * Support email shown on every `/redeem/invalid` failure. Single source —
 * if it ever needs to vary by partner, lift to the partner config.
 */
export const HELP_EMAIL = 'partnersupport@ouraring.com';

/**
 * Translation keys for each failure mode the `/redeem/invalid` page can
 * land on. The reason string is set by the redirector (validate page or
 * protected layout) and read off the URL query at render time.
 *
 * Two of these are API-driven (returned by `validateRedeemCode` when
 * `success: 'false'`) and one is client-driven (set by the protected
 * layout when the API returned a flow type the client doesn't know how
 * to render). `resolveInvalidReason` below gates URL input against this
 * map and falls back to `invalid_or_expired_code` for unknown / missing
 * values so users never see a blank page.
 */
export const REDEEM_INVALID_REASONS = {
  invalid_or_expired_code: {
    titleKey: 'eop_redeem_invalid_invalid_or_expired_code_title',
    descriptionKey: 'eop_redeem_invalid_invalid_or_expired_code_description',
  },
  code_already_redeemed: {
    titleKey: 'eop_redeem_invalid_code_already_redeemed_title',
    descriptionKey: 'eop_redeem_invalid_code_already_redeemed_description',
  },
  unknown_flow: {
    titleKey: 'eop_redeem_invalid_invalid_or_expired_code_title',
    descriptionKey: 'eop_redeem_invalid_invalid_or_expired_code_description',
  },
} as const satisfies Record<
  string,
  { titleKey: string; descriptionKey: string }
>;

export type RedeemInvalidReason = keyof typeof REDEEM_INVALID_REASONS;

/**
 * Subset of `RedeemInvalidReason`s that the `validateRedeemCode` API
 * can return on a `success: 'false'` response. Used by the API contract
 * type so a future API change can't surface a reason the client doesn't
 * know how to render.
 */
export type ApiFailureReason = Extract<
  RedeemInvalidReason,
  'invalid_or_expired_code' | 'code_already_redeemed'
>;

/**
 * Narrows a raw URL query value (`?reason=...`) to a known reason, or
 * falls back to `invalid_or_expired_code` for unknown / missing values.
 * Centralized here so swapping the fallback (or adding validation
 * logging) is a one-line change.
 */
export const resolveInvalidReason = (
  rawReason: string | null | undefined,
): RedeemInvalidReason =>
  rawReason && rawReason in REDEEM_INVALID_REASONS
    ? (rawReason as RedeemInvalidReason)
    : 'invalid_or_expired_code';
